import { useState, useCallback, useEffect } from 'react';
import { AppliedModifierFormData, createDefaultFormData } from '../constant/applied_modifiers_constants';
import { AppliedModifierValidator } from '../utility/applied_modifier_validator';
import * as AppliedModifiersConstants from '../constant/applied_modifiers_constants';

/**
 * Interface for form state management
 */
export interface IFormStateManager {
  formData: AppliedModifierFormData;
  updateField: (field: string, value: any) => void;
  validateField: (field: string) => boolean;
  isFormValid: () => boolean;
  isSubmitted: boolean;
  setIsSubmitted: (submitted: boolean) => void;
  resetForm: () => void;
}

/**
 * Custom hook for managing Applied Modifiers form state
 */
export const useAppliedModifiersForm = (
  initialData?: AppliedModifierFormData
): IFormStateManager => {
  const [formData, setFormData] = useState<AppliedModifierFormData>(
    initialData || createDefaultFormData()
  );
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Initialize form data if edit data is provided
  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);

  const updateField = useCallback((field: string, value: any) => {
    setFormData(prevData => ({
      ...prevData,
      [field]: value
    }));
  }, []);

  const validateField = useCallback((fieldName: string): boolean => {
    const result = AppliedModifierValidator.validateField(fieldName, formData, isSubmitted);
    return result.isValid;
  }, [formData, isSubmitted]);

  const isFormValid = useCallback((): boolean => {
    const result = AppliedModifierValidator.validateForm(formData, AppliedModifiersConstants.BASE_REQUIRED_FIELDS);
    return result.isValid;
  }, [formData]);

  const resetForm = useCallback(() => {
    setFormData(createDefaultFormData());
    setIsSubmitted(false);
  }, []);

  return {
    formData,
    updateField,
    validateField,
    isFormValid,
    isSubmitted,
    setIsSubmitted,
    resetForm
  };
};

/**
 * Hook for managing field-specific validation display
 */
export const useFieldValidation = (formData: AppliedModifierFormData, isSubmitted: boolean) => {
  const markFieldForRequiredValidation = useCallback((fieldName: string): boolean => {
    return AppliedModifierValidator.shouldShowRequiredValidation(fieldName, formData, isSubmitted);
  }, [formData, isSubmitted]);

  const markFieldForValidation = useCallback((fieldName: string): boolean => {
    return AppliedModifierValidator.shouldShowRequiredValidation(fieldName, formData, isSubmitted);
  }, [formData, isSubmitted]);

  return {
    markFieldForRequiredValidation,
    markFieldForValidation
  };
};
